import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { apiGet, apiPost } from "./client";
import { fetchJobs, jobSchema, type JobOut } from "./sync";

/**
 * `GET /api/v2/denetim/katalog`, `POST /api/v2/denetim/katalog/tara` — sozlesme
 * §4 (backend/app/core/api/denetim.py + app/core/audit.py `run_catalog_audit`).
 */

const bulguSchema = z
  .object({
    urun_kart_id: z.union([z.string(), z.number()]).nullable().optional(),
    ad: z.string().nullable().optional(),
    kategori: z.string().nullable().optional(),
    stok: z.number().nullable().optional(),
    slug: z.string().nullable().optional(),
    sayfa_durumu: z.string().nullable().optional(),
    mevcut_ozellik_degeri: z.string().nullable().optional(),
    kaynak: z.string().nullable().optional(),
    rozet_var: z.boolean().nullable().optional(),
    kanit: z.string().nullable().optional(),
    /** adapter.admin_link henuz bu ucta doldurulmuyor (bkz. api/stok.ts ayni not). */
    admin_link: z.string().nullable().optional(),
  })
  .passthrough();
export type DenetimBulgu = z.infer<typeof bulguSchema>;

/** `app/audit/catalog.py katalog_denetimi()` — bulgular sozlugunun 4 sabit anahtari. */
export const DENETIM_BULGU_TURLERI = ["eksik_ozellik", "rozet_uyusmazlik", "gri_alan", "celiski"] as const;
export type DenetimBulguTuru = (typeof DENETIM_BULGU_TURLERI)[number];

const findingsSchema = z.record(z.string(), z.array(bulguSchema));
export type DenetimFindings = z.infer<typeof findingsSchema>;

const summarySchema = z.record(z.string(), z.unknown());
export type DenetimSummary = z.infer<typeof summarySchema>;

const denetimReadySchema = z.object({
  status: z.literal("ready"),
  id: z.union([z.string(), z.number()]).nullable().optional(),
  run_at: z.string().nullable().optional(),
  summary: summarySchema,
  findings: findingsSchema,
});
export type DenetimReady = z.infer<typeof denetimReadySchema>;

const denetimNoneSchema = z.object({ status: z.literal("none") });
const notSupportedSchema = z.object({ status: z.literal("not_supported") });

export const denetimKatalogResponseSchema = z.union([
  denetimReadySchema,
  denetimNoneSchema,
  notSupportedSchema,
]);
export type DenetimKatalogResponse = z.infer<typeof denetimKatalogResponseSchema>;

export function fetchKatalog(): Promise<DenetimKatalogResponse> {
  return apiGet("/api/v2/denetim/katalog", denetimKatalogResponseSchema);
}

export const KATALOG_QUERY_KEY = ["denetim", "katalog"] as const;

export function useKatalog() {
  return useQuery({ queryKey: KATALOG_QUERY_KEY, queryFn: fetchKatalog });
}

const taraResponseSchema = z.object({ job_id: z.number().nullable() });
export type TaraResponse = z.infer<typeof taraResponseSchema>;

/**
 * POST /denetim/katalog/tara govdesi — eski `legacy/denetim.js kuralOku()`
 * ile ayni alan adlari (gorev A: katalog denetimi kural paneli). TUMU
 * opsiyonel; backend (app/core/api/denetim.py KatalogKuralRequest) verilmeyen
 * alan icin kendi varsayilanini kullanir.
 */
export interface KatalogKuralPayload {
  kelime?: string;
  yuzde?: number;
  ozellik_adi?: string;
  rozet_dosyasi?: string;
  kategoriler?: string[];
  urun_adi_kontrol?: boolean;
  gri_alan?: boolean;
  satisa_kapali_dahil?: boolean;
}

export function postKatalogTara(kural?: KatalogKuralPayload): Promise<TaraResponse> {
  return apiPost("/api/v2/denetim/katalog/tara", taraResponseSchema, kural);
}

export function useKatalogTaraMutation() {
  return useMutation({ mutationFn: (kural?: KatalogKuralPayload) => postKatalogTara(kural) });
}

/** Gorev sozlesmesindeki kanca adi (P5-F3 spesifikasyonu §1) — ayni kanca. */
export const useKatalogTara = useKatalogTaraMutation;

// ---------------------------------------------------------------------------
// GET /denetim/kategoriler — kural formundaki kategori coklu-secimi (gorev A).
// ---------------------------------------------------------------------------

const kategorilerResponseSchema = z.object({ kategoriler: z.array(z.string()) });

export function fetchDenetimKategoriler(): Promise<string[]> {
  return apiGet("/api/v2/denetim/kategoriler", kategorilerResponseSchema).then((res) => res.kategoriler);
}

export const KATEGORILER_QUERY_KEY = ["denetim", "kategoriler"] as const;

export function useDenetimKategoriler() {
  return useQuery({ queryKey: KATEGORILER_QUERY_KEY, queryFn: fetchDenetimKategoriler });
}

// ---------------------------------------------------------------------------
// useJob — /api/v2/jobs listesinden id ile is durumu takibi (sozlesme: sabit
// 5 sn, sekme gizliyken durur). Terminal durumda (done/failed/cancelled)
// polling KENDILIGINDEN durur — TanStack Query'nin `refetchInterval` fonksiyon
// formu (v5) her tikte son veriye bakip yeni interval'i (ya da `false`) hesaplar.
// Sekme gorunurlugu: `refetchIntervalInBackground:false` (varsayilan) +
// TanStack'in kendi `focusManager`'i (bkz. Freshness.tsx/api/sync.ts ayni
// kalip) — burada AYRICA bir visibilitychange dinleyicisi KURULMAZ.
// ---------------------------------------------------------------------------

const TERMINAL_STATUSES = new Set(["done", "failed", "cancelled"]);

export { jobSchema };
export type { JobOut };

export const JOB_POLL_MS = 5000;

export interface UseJobResult {
  job: JobOut | undefined;
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
}

/**
 * `intervalMs`: uretimde HER ZAMAN varsayilan (5 sn, sozlesme) — yalniz
 * testlerin gercek zamanlayicilarla (fake timers YOK) kisa surede
 * dogrulayabilmesi icin disaridan gecilebilir hale getirilmistir.
 */
export function useJob(jobId: number | null, intervalMs: number = JOB_POLL_MS): UseJobResult {
  const query = useQuery({
    queryKey: ["jobs", "poll", jobId] as const,
    queryFn: () => fetchJobs(100),
    enabled: jobId != null,
    refetchInterval: (q) => {
      const jobs = q.state.data?.jobs ?? [];
      const current = jobs.find((j) => j.id === jobId);
      if (current && TERMINAL_STATUSES.has(current.status)) return false;
      return intervalMs;
    },
    refetchIntervalInBackground: false,
  });

  const job = jobId == null ? undefined : query.data?.jobs.find((j) => j.id === jobId);
  return { job, isLoading: query.isLoading, isError: query.isError, isFetching: query.isFetching };
}

export function isTerminalJobStatus(status: string | undefined): boolean {
  return status != null && TERMINAL_STATUSES.has(status);
}

export function useInvalidateKatalog() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: KATALOG_QUERY_KEY });
}
